import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { UseMutationOptions, useQueryClient } from 'react-query';
import http from '../config/http-service';
import type { CreateRestaurantInquiryItem, RestaurantInquiry } from './types';
import { restaurantInquiriesEndpoint } from './use-create-restaurant-inquiry-api';

// POST ums/v1/cms/restaurant-inquiries/:id/items — append items to an existing
// inquiry. Returns the whole updated inquiry (same nested shape as GET :id), so
// callers can replace local state instead of merging. NOT idempotent: retrying
// a timed-out request duplicates items (no dedup on masterProductId).
type TData = RestaurantInquiry;
type TError = AxiosError;
type TVariables = { id: number | string; items: CreateRestaurantInquiryItem[] };

type Options = Omit<UseMutationOptions<TData, TError, TVariables>, 'mutationFn'>;
type TReturnType = ReturnType<typeof useMutationWrapper<TData, TError, TVariables>>;

export function useAddRestaurantInquiryItemsApi(options?: Options): TReturnType {
  const queryClient = useQueryClient();
  const { onSuccess: userOnSuccess, ...restOptions } = options || {};

  const mutationFn = async ({ id, items }: TVariables): Promise<TData> => {
    const response = await http.post(`${restaurantInquiriesEndpoint}/${id}/items`, { items });
    // The API wraps the updated resource in an envelope: { data: {...} }.
    return response.data?.data ?? response.data;
  };

  return useMutationWrapper(mutationFn, {
    mutationKey: [restaurantInquiriesEndpoint, 'add-items'],
    onSuccess: (data, variables, context) => {
      // Refresh the list and this inquiry's detail view.
      queryClient.invalidateQueries([restaurantInquiriesEndpoint]);
      userOnSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
}
